import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import { formatDate } from '../../../utils/formatters';

export default function DepartmentDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const departmentQuery = useQuery({
    queryKey: ['department', id],
    queryFn: () => customerService.getDepartment(id).then((res) => res.data),
  });

  const department = departmentQuery.data;

  // Deletes straight away and lets the list reopen the undo snackbar via
  // `seedDelete` — the backend soft-deletes, so "Desfazer" there can still
  // restore this exact row (uuid and internal_name included).
  const handleDelete = async () => {
    await customerService.deleteDepartment(id);
    queryClient.invalidateQueries({ queryKey: ['departments'] });
    navigate('/departments', {
      state: { seedDelete: { ids: [id], message: t('departments.deleteSuccess') } },
    });
  };

  const header = (
    <PageHeader
      title={department?.name ?? t('departments.detailTitle')}
      breadcrumbs={[
        { label: t('departments.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('departments.breadcrumb.list'), to: '/departments' },
        { label: department?.name ?? t('departments.detailTitle') },
      ]}
    />
  );

  const renderBody = () => {
    if (departmentQuery.isLoading) {
      return <LoadingState message={t('departments.states.loading')} />;
    }

    if (departmentQuery.isError) {
      const status = departmentQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('departments.states.permissionDeniedTitle')}
            message={t('departments.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState
            title={t('departments.states.notFoundTitle')}
            message={t('departments.states.notFoundMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('departments.states.errorTitle')}
          message={t('departments.states.errorMessage')}
          actionLabel={t('departments.states.retry')}
          onRetry={departmentQuery.refetch}
        />
      );
    }

    return (
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('departments.sections.identification')}
            subheader={t('departments.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('departments.fields.name')}
                  secondary={department.name}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('departments.fields.internalName')}
                  secondary={department.internal_name}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontFamily: 'monospace' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('departments.fields.description')}
                  secondary={department.description || '—'}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary={t('departments.fields.createdAt')}
                  secondary={formatDate(department.created_at, i18n.language)}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
            </List>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
              {t('departments.actions.delete')}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate(`/departments/${id}/edit`)}
            >
              {t('departments.actions.edit')}
            </Button>
          </CardActions>
        </Card>
      </Stack>
    );
  };

  return <BoundedPageLayout header={header}>{renderBody()}</BoundedPageLayout>;
}
